import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, AuthProvider } from "@/context/AuthContext";
import AdminLogin from "@/components/AdminLogin";
import {
  Search,
  RefreshCw,
  Calendar,
  Clock,
  Mail,
  Phone,
  MapPin,
  Loader2,
  LayoutDashboard,
  CalendarDays,
  Users,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  Download,
  LogOut,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardContent = () => {
  const { admin, logout, getAuthHeader, isLoading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    if (admin) {
      fetchData();
    }
  }, [statusFilter, serviceFilter, searchQuery, admin]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (serviceFilter !== "all") params.append("service_type", serviceFilter);
      if (searchQuery) params.append("search", searchQuery);

      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/bookings?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/stats`, { headers: getAuthHeader() }),
      ]);

      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        logout();
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(
        `${API}/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(`Booking status updated to ${newStatus}`);
      fetchData();
      setSelectedBooking(null);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        logout();
      } else {
        toast.error("Failed to update status");
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const exportToCSV = () => {
    const headers = ["ID", "Name", "Email", "Phone", "Service", "Date", "Time", "Status", "Created"];
    const rows = bookings.map((b) => [
      b.id,
      b.name,
      b.email,
      b.phone,
      b.service_type,
      b.preferred_date,
      b.preferred_time,
      b.status,
      b.created_at,
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bookings-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success("CSV exported successfully");
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-100 text-blue-800",
      in_progress: "bg-amber-100 text-amber-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return (
      <Badge className={`${styles[status] || "bg-slate-100 text-slate-800"}`}>
        {status.replace("_", " ")}
      </Badge>
    );
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card className="border-slate-100">
      <CardContent className="p-6 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
        </div>
      </CardContent>
    </Card>
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!admin) {
    return <AdminLogin />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sky-600 rounded-xl flex items-center justify-center text-white">
                <LayoutDashboard className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-slate-900">Admin Dashboard</h1>
                <p className="text-xs text-slate-500">Logged in as {admin.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={fetchData}
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                onClick={logout}
                variant="outline"
                size="sm"
                className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard
              title="Total Bookings"
              value={stats.total_bookings}
              icon={CalendarDays}
              color="bg-slate-100 text-slate-600"
            />
            <StatCard
              title="New"
              value={stats.new_bookings}
              icon={AlertCircle}
              color="bg-blue-100 text-blue-600"
            />
            <StatCard
              title="In Progress"
              value={stats.in_progress}
              icon={Clock}
              color="bg-amber-100 text-amber-600"
            />
            <StatCard
              title="Completed"
              value={stats.completed}
              icon={CheckCircle2}
              color="bg-green-100 text-green-600"
            />
          </div>
        )}

        {/* Filters */}
        <Card className="border-slate-100 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="admin-search-input"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger className="w-[150px]" data-testid="service-filter">
                  <SelectValue placeholder="Service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  <SelectItem value="wash-fold">Wash & Fold</SelectItem>
                  <SelectItem value="dry-cleaning">Dry Cleaning</SelectItem>
                  <SelectItem value="fresh-gear">Fresh Gear</SelectItem>
                  <SelectItem value="pickup-delivery">Pickup & Delivery</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={exportToCSV} variant="outline" className="gap-2" data-testid="export-btn">
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Table */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Bookings ({bookings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CalendarDays className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No bookings found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="booking-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{booking.name}</p>
                            <p className="text-sm text-slate-500">{booking.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="capitalize">{booking.service_type.replace("-", " ")}</span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{booking.preferred_date}</p>
                            <p className="text-slate-500">{booking.preferred_time}</p>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(booking.status)}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedBooking(booking)}
                            data-testid={`view-booking-${booking.id}`}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Booking Detail Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription>View and manage this booking</DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900">Customer Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span>{selectedBooking.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${selectedBooking.email}`} className="text-sky-600 hover:underline">
                      {selectedBooking.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${selectedBooking.phone}`} className="text-sky-600 hover:underline">
                      {selectedBooking.phone}
                    </a>
                  </div>
                  {selectedBooking.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{selectedBooking.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Booking Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900">Booking Details</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Service:</span>
                    <span className="capitalize">{selectedBooking.service_type.replace("-", " ")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date:</span>
                    <span>{selectedBooking.preferred_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Time:</span>
                    <span>{selectedBooking.preferred_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status:</span>
                    {getStatusBadge(selectedBooking.status)}
                  </div>
                </div>
                {selectedBooking.notes && (
                  <div className="bg-amber-50 rounded-lg p-4 text-sm">
                    <p className="font-medium text-amber-800 mb-1">Notes:</p>
                    <p className="text-amber-700">{selectedBooking.notes}</p>
                  </div>
                )}
              </div>

              {/* Status Update */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900">Update Status</h3>
                <div className="flex flex-wrap gap-2">
                  {["new", "in_progress", "completed", "cancelled"].map((status) => (
                    <Button
                      key={status}
                      variant={selectedBooking.status === status ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateBookingStatus(selectedBooking.id, status)}
                      disabled={isUpdatingStatus || selectedBooking.status === status}
                      className={selectedBooking.status === status ? "bg-sky-600" : ""}
                      data-testid={`status-btn-${status}`}
                    >
                      {isUpdatingStatus ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        status.replace("_", " ")
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminDashboard = () => {
  return (
    <AuthProvider>
      <AdminDashboardContent />
    </AuthProvider>
  );
};

export default AdminDashboard;
